import {
  DEFAULT_QUICK_MESSAGE_GROUP_ID,
  DEFAULT_QUICK_MESSAGE_GROUP_NAME,
  QuickMessageConfig,
  QuickMessageGroup,
  QuickMessageItem,
  createQuickMessageId,
  getNextQuickMessageGroupOrder,
  getNextQuickMessageOrder,
  normalizeQuickMessageConfig,
  normalizeQuickMessageText,
  serializeQuickMessageConfig,
  importQuickMessageConfig,
  QuickMessageImportError,
} from './quick-message-schema';
import { copyText } from './chat/clipboard';

type QuickMessagesControllerOptions = {
  storageKey: string;
  limit: number;
  getAuthToken: () => string | null;
  send: (payload: any) => void;
  showToast: (message: string) => void;
  panel: HTMLElement | null;
  list: HTMLElement | null;
  toggle: HTMLElement | null;
  saveButton: HTMLButtonElement | null;
  groupTabs: HTMLElement | null;
  addGroupButton: HTMLButtonElement | null;
  exportButton: HTMLButtonElement | null;
  importButton: HTMLButtonElement | null;
  importInput: HTMLInputElement | null;
  input: HTMLTextAreaElement;
  autoResize: () => void;
  sendMessage: () => void;
};

const ACTIVE_GROUP_STORAGE_KEY = 'webcoding-quick-message-active-group';
const QUICK_MESSAGE_EXPORT_VERSION = 1;

function getLocalDateString(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// 下载文本文件，统一处理临时 URL 的创建和释放。
function downloadTextFile(filename: string, content: string): void {
  const blob = new Blob([content], { type: 'application/json;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

function loadQuickMessageConfig(storageKey: string, limit: number): QuickMessageConfig {
  try {
    const parsed = JSON.parse(localStorage.getItem(storageKey) || '[]');
    return normalizeQuickMessageConfig(parsed, limit);
  } catch {
    return { groups: [], items: [] };
  }
}

function getStoredActiveGroupId(): string {
  try {
    return localStorage.getItem(ACTIVE_GROUP_STORAGE_KEY) || DEFAULT_QUICK_MESSAGE_GROUP_ID;
  } catch {
    return DEFAULT_QUICK_MESSAGE_GROUP_ID;
  }
}

function storeActiveGroupId(groupId: string): void {
  try {
    localStorage.setItem(ACTIVE_GROUP_STORAGE_KEY, groupId || DEFAULT_QUICK_MESSAGE_GROUP_ID);
  } catch {}
}

function hasExplicitQuickMessageConfigPayload(payload: any): boolean {
  return Array.isArray(payload)
    || Array.isArray(payload?.items)
    || Array.isArray(payload?.groups);
}

export function createQuickMessagesController(options: QuickMessagesControllerOptions) {
  let config = loadQuickMessageConfig(options.storageKey, options.limit);
  let activeGroupId = getStoredActiveGroupId();
  let panelOpen = false;
  let loadedFromServer = false;
  let openItemMenuId: string | null = null;

  function persist() {
    localStorage.setItem(options.storageKey, JSON.stringify(serializeQuickMessageConfig(config, options.limit)));
  }

  // 导出当前规范化配置，保留分组、排序和立即发送状态。
  function exportConfig(): void {
    const payload = { version: QUICK_MESSAGE_EXPORT_VERSION, ...serializeQuickMessageConfig(config, options.limit) };
    downloadTextFile(
      `webcoding-quick-messages-${getLocalDateString()}.json`,
      `${JSON.stringify(payload, null, 2)}\n`,
    );
    options.showToast('快捷消息已导出为 JSON');
  }

  // 读取并严格校验单个 JSON 文件，校验失败时不改变现有配置。
  async function importConfigFile(file: File): Promise<void> {
    try {
      const payload = JSON.parse(await file.text());
      const result = importQuickMessageConfig(config, payload, options.limit);
      config = result.config;
      persist();
      render();
      syncToServer();
      options.showToast(`快捷消息导入完成：新增 ${result.addedItems} 条，新增 ${result.addedGroups} 个分组，重复跳过 ${result.skippedItems} 条，超限未导入 ${result.limitedItems} 条`);
    } catch (error) {
      if (error instanceof QuickMessageImportError) {
        options.showToast(`快捷消息导入失败：${error.message}`);
        return;
      }
      console.error('[quick-messages] import failed', error);
      options.showToast('快捷消息导入失败：文件读取或格式解析异常');
    }
  }

  function syncToServer(syncOptions: any = {}) {
    if (!options.getAuthToken()) return;
    if (!syncOptions.force && !loadedFromServer) return;
    const next = serializeQuickMessageConfig(config, options.limit);
    options.send({ type: 'save_quick_messages', groups: next.groups, items: next.items });
  }

  function normalizeState(): void {
    config = normalizeQuickMessageConfig(config, options.limit);
    const groupExists = activeGroupId === DEFAULT_QUICK_MESSAGE_GROUP_ID
      || config.groups.some((group) => group.id === activeGroupId);
    if (!groupExists) activeGroupId = DEFAULT_QUICK_MESSAGE_GROUP_ID;
    if (openItemMenuId && !config.items.some((item) => item.id === openItemMenuId)) openItemMenuId = null;
    storeActiveGroupId(activeGroupId);
  }

  function setActiveGroup(groupId: string | null): void {
    activeGroupId = groupId || DEFAULT_QUICK_MESSAGE_GROUP_ID;
    normalizeState();
    render();
  }

  function getActiveGroupIdForItem(): string | null {
    return activeGroupId === DEFAULT_QUICK_MESSAGE_GROUP_ID ? null : activeGroupId;
  }

  function getGroupName(groupId: string | null): string {
    if (!groupId) return DEFAULT_QUICK_MESSAGE_GROUP_NAME;
    return config.groups.find((group) => group.id === groupId)?.name || DEFAULT_QUICK_MESSAGE_GROUP_NAME;
  }

  function promptGroupName(title: string, defaultValue = ''): string {
    return String(window.prompt(title, defaultValue) || '').trim().slice(0, 40);
  }

  function createGroup(): void {
    const name = promptGroupName('输入分组名称');
    if (!name) return;
    const group: QuickMessageGroup = {
      id: createQuickMessageId('grp'),
      name,
      order: getNextQuickMessageGroupOrder(config.groups),
    };
    config.groups = [...config.groups, group];
    activeGroupId = group.id;
    persist();
    render();
    syncToServer();
    options.showToast('已创建快捷消息分组');
  }

  function renameGroup(group: QuickMessageGroup): void {
    const name = promptGroupName('重命名分组', group.name);
    if (!name || name === group.name) return;
    config.groups = config.groups.map((entry) => entry.id === group.id ? { ...entry, name } : entry);
    persist();
    render();
    syncToServer();
    options.showToast('已重命名分组');
  }

  function deleteGroup(group: QuickMessageGroup): void {
    if (!window.confirm(`删除分组「${group.name}」？组内快捷消息将移动到未分组。`)) return;
    config.groups = config.groups.filter((entry) => entry.id !== group.id);
    config.items = config.items.map((item) => item.groupId === group.id ? { ...item, groupId: null } : item);
    if (activeGroupId === group.id) activeGroupId = DEFAULT_QUICK_MESSAGE_GROUP_ID;
    persist();
    render();
    syncToServer();
    options.showToast('已删除分组，消息已移动到未分组');
  }

  function moveItem(item: QuickMessageItem): void {
    const groups = [{ id: DEFAULT_QUICK_MESSAGE_GROUP_ID, name: DEFAULT_QUICK_MESSAGE_GROUP_NAME }, ...config.groups];
    const label = groups.map((group, index) => `${index + 1}. ${group.name}`).join('\n');
    const raw = window.prompt(`移动到哪个分组？\n${label}`, '1');
    const index = Number(raw) - 1;
    if (!Number.isInteger(index) || index < 0 || index >= groups.length) return;
    const targetGroupId = groups[index].id || null;
    config.items = config.items.map((entry) => entry.id === item.id
      ? { ...entry, groupId: targetGroupId, order: getNextQuickMessageOrder(config.items, targetGroupId) }
      : entry);
    persist();
    render();
    syncToServer();
    options.showToast(`已移动到${getGroupName(targetGroupId)}`);
  }

  function removeItem(item: QuickMessageItem): void {
    if (!window.confirm('删除这条快捷消息？')) return;
    config.items = config.items.filter((entry) => entry.id !== item.id);
    openItemMenuId = null;
    persist();
    render();
    syncToServer();
    options.showToast('已删除快捷消息');
  }

  function toggleImmediateSend(item: QuickMessageItem): void {
    const nextImmediateSend = item.immediateSend !== true;
    config.items = config.items.map((entry) => entry.id === item.id
      ? { ...entry, immediateSend: nextImmediateSend }
      : entry);
    openItemMenuId = null;
    persist();
    render();
    syncToServer();
    options.showToast(nextImmediateSend ? '已设为立即发送' : '已取消立即发送');
  }

  function copyItem(item: QuickMessageItem): void {
    openItemMenuId = null;
    render();
    copyText(item.text, () => options.showToast('已复制快捷消息'), () => options.showToast('复制失败'));
  }

  function applyItemAction(item: QuickMessageItem, action: string): void {
    if (action === 'copy') {
      copyItem(item);
    } else if (action === 'move') {
      openItemMenuId = null;
      moveItem(item);
    } else if (action === 'toggle-immediate') {
      toggleImmediateSend(item);
    } else if (action === 'remove') {
      removeItem(item);
    }
  }

  function createItemActionButton(item: QuickMessageItem, action: string, label: string): HTMLButtonElement {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `quick-message-action-option${action === 'remove' ? ' danger' : ''}`;
    button.textContent = label;
    button.addEventListener('click', (event) => {
      event.stopPropagation();
      applyItemAction(item, action);
    });
    return button;
  }

  function renderTabs() {
    if (!options.groupTabs) return;
    options.groupTabs.innerHTML = '';
    const allGroups = [
      { id: DEFAULT_QUICK_MESSAGE_GROUP_ID, name: DEFAULT_QUICK_MESSAGE_GROUP_NAME, order: -1, isDefault: true },
      ...config.groups.map((group) => ({ ...group, isDefault: false })),
    ];
    for (const group of allGroups) {
      const tab = document.createElement('button');
      tab.type = 'button';
      tab.className = `quick-message-group-tab${activeGroupId === group.id ? ' active' : ''}`;
      tab.textContent = group.name;
      tab.title = group.name;
      tab.addEventListener('click', (event) => {
        event.stopPropagation();
        setActiveGroup(group.id);
      });
      options.groupTabs.appendChild(tab);

      if (!group.isDefault) {
        const menu = document.createElement('button');
        menu.type = 'button';
        menu.className = 'quick-message-group-menu';
        menu.textContent = '⋯';
        menu.title = '分组操作';
        menu.addEventListener('click', (event) => {
          event.stopPropagation();
          const action = window.prompt('输入操作编号：\n1. 重命名分组\n2. 删除分组', '1');
          if (action === '1') renameGroup(group);
          else if (action === '2') deleteGroup(group);
        });
        options.groupTabs.appendChild(menu);
      }
    }
  }

  function renderList() {
    if (!options.list) return;
    options.list.innerHTML = '';
    const currentGroupId = getActiveGroupIdForItem();
    const visibleItems = config.items.filter((item) => (item.groupId || null) === currentGroupId);
    if (visibleItems.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'quick-message-empty';
      empty.textContent = config.items.length === 0
        ? '还没有快捷消息。你可以保存当前输入，或在历史消息里点“加入快捷消息”。'
        : '当前分组还没有快捷消息。';
      options.list.appendChild(empty);
      return;
    }

    visibleItems.forEach((item) => {
      const row = document.createElement('div');
      row.className = 'quick-message-item';

      const chip = document.createElement('button');
      chip.type = 'button';
      chip.className = `quick-message-chip${item.immediateSend ? ' immediate-send' : ''}`;
      chip.textContent = item.text.length > 50 ? `${item.text.slice(0, 50)}…` : item.text;
      chip.title = item.immediateSend ? `立即发送：${item.text}` : item.text;
      chip.addEventListener('click', () => {
        options.input.value = item.text;
        options.autoResize();
        if (item.immediateSend) {
          panelOpen = false;
          render();
          options.sendMessage();
          return;
        }
        options.input.focus();
        render();
      });

      const actionWrap = document.createElement('div');
      actionWrap.className = 'quick-message-action-wrap';
      const actionButton = document.createElement('button');
      actionButton.type = 'button';
      actionButton.className = 'quick-message-action-menu';
      actionButton.textContent = '⋯';
      actionButton.title = '快捷消息操作';
      actionButton.setAttribute('aria-label', '快捷消息操作');
      actionButton.setAttribute('aria-expanded', openItemMenuId === item.id ? 'true' : 'false');
      actionButton.addEventListener('click', (event) => {
        event.stopPropagation();
        openItemMenuId = openItemMenuId === item.id ? null : item.id;
        render();
      });
      actionWrap.appendChild(actionButton);

      if (openItemMenuId === item.id) {
        const actionPanel = document.createElement('div');
        actionPanel.className = 'quick-message-action-panel';
        actionPanel.appendChild(createItemActionButton(item, 'copy', '复制'));
        actionPanel.appendChild(createItemActionButton(item, 'move', '移动到分组'));
        actionPanel.appendChild(createItemActionButton(item, 'toggle-immediate', item.immediateSend ? '取消立即发送' : '设为立即发送'));
        actionPanel.appendChild(createItemActionButton(item, 'remove', '删除'));
        actionWrap.appendChild(actionPanel);
      }

      row.appendChild(chip);
      row.appendChild(actionWrap);
      options.list?.appendChild(row);
    });
  }

  function render() {
    normalizeState();
    if (!options.panel || !options.list || !options.toggle || !options.saveButton) return;
    options.panel.hidden = !panelOpen;
    options.toggle.setAttribute('aria-expanded', panelOpen ? 'true' : 'false');
    options.saveButton.disabled = !normalizeQuickMessageText(options.input?.value || '');
    renderTabs();
    renderList();
  }

  options.addGroupButton?.addEventListener('click', () => createGroup());
  options.exportButton?.addEventListener('click', () => exportConfig());
  options.importButton?.addEventListener('click', () => options.importInput?.click());
  options.importInput?.addEventListener('change', () => {
    const file = options.importInput?.files?.[0];
    if (file) void importConfigFile(file);
    if (options.importInput) options.importInput.value = '';
  });

  return {
    render,
    has(text = '') {
      const normalized = normalizeQuickMessageText(text);
      return !!normalized && config.items.some((item) => item.text === normalized);
    },
    save(text = '', saveOptions: any = {}) {
      const normalized = normalizeQuickMessageText(text);
      if (!normalized) {
        if (!saveOptions.silent) options.showToast('没有可保存的内容');
        return false;
      }
      const existing = config.items.find((item) => item.text === normalized);
      const targetGroupId = saveOptions.source === 'history'
        ? existing?.groupId || null
        : getActiveGroupIdForItem();
      const nextOrder = getNextQuickMessageOrder(config.items, targetGroupId);
      if (existing) {
        config.items = config.items.map((item) => item.id === existing.id
          ? { ...item, groupId: targetGroupId, order: nextOrder }
          : item);
      } else {
        config.items = [{ id: createQuickMessageId('qm'), text: normalized, groupId: targetGroupId, order: nextOrder, immediateSend: false }]
          .concat(config.items)
          .slice(0, options.limit);
      }
      persist();
      render();
      syncToServer();
      if (!saveOptions.silent) {
        options.showToast(existing
          ? (saveOptions.source === 'history' ? '已置顶到快捷消息' : '已移动到当前分组并置顶')
          : '已加入快捷消息');
      }
      return true;
    },
    setPanelOpen(open: boolean) {
      panelOpen = !!open;
      render();
    },
    isPanelOpen() {
      return panelOpen;
    },
    applyFromServer(payload: any = {}) {
      const normalized = normalizeQuickMessageConfig(payload, options.limit);
      loadedFromServer = true;
      if (!hasExplicitQuickMessageConfigPayload(payload) && normalized.items.length === 0 && config.items.length > 0) {
        syncToServer({ force: true });
        return;
      }
      config = normalized;
      persist();
      render();
    },
  };
}
